"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, TopBar } from "@/components/ui";
import { SignInModal } from "@/components/SignInModal";
import { useAddresses } from "@/hooks/useAddresses";
import { useAuth } from "@/store/auth";
import type { AppSchemasCustomerAddress } from "@/api/online-ordering-service/model";

function inferLabel(a: AppSchemasCustomerAddress): "Home" | "Work" | "Other" {
  const n = (a.name ?? "").toLowerCase();
  if (n.includes("home")) return "Home";
  if (n.includes("work") || n.includes("office")) return "Work";
  return "Other";
}

function formatLine(a: AppSchemasCustomerAddress): string {
  return [a.street, a.street2, a.city, a.state, a.postalCode]
    .filter((p): p is string => !!p && p.length > 0)
    .join(", ");
}

export function SavedAddressesScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { addresses, loading, error, deleteAddress } = useAddresses();
  const [signInOpen, setSignInOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 100 }}>
        <TopBar title={t("v2.address.list_title")} onBack={() => navigate(-1)} />
        <div
          style={{
            padding: "32px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              color: "var(--ink-2)",
            }}
          >
            <Icons.pin size={24} />
          </div>
          <div className="display" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.address.list_signin_title")}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              maxWidth: 280,
              lineHeight: 1.45,
            }}
          >
            {t("v2.address.list_signin_desc")}
          </div>
          <Button size="lg" onClick={() => setSignInOpen(true)}>
            {t("v2.history.sign_in")}
          </Button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar title={t("v2.address.list_title")} onBack={() => navigate(-1)} />
      <div
        style={{
          padding: "8px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {loading && addresses.length === 0 && (
          <>
            {[0, 1].map((i) => (
              <Card key={i} padding={14}>
                <div
                  style={{
                    height: 14,
                    width: "40%",
                    background: "var(--surface-2)",
                    borderRadius: 6,
                  }}
                />
                <div
                  style={{
                    height: 12,
                    width: "70%",
                    background: "var(--surface-2)",
                    borderRadius: 6,
                    marginTop: 8,
                  }}
                />
              </Card>
            ))}
          </>
        )}

        {error && !loading && (
          <div
            style={{
              fontSize: 13,
              color: "#b91c1c",
              padding: "10px 12px",
              background: "#fee2e2",
              borderRadius: 10,
            }}
          >
            {t("v2.address.list_couldnt_load")} {error.message}
          </div>
        )}

        {!loading && addresses.length === 0 && !error && (
          <div
            style={{
              padding: "24px 12px",
              textAlign: "center",
              color: "var(--ink-2)",
              fontSize: 13,
            }}
          >
            {t("v2.address.list_empty")}
          </div>
        )}

        {addresses.map((a) => {
          const label = inferLabel(a);
          const Icon =
            label === "Home" ? Icons.home : label === "Work" ? Icons.store : Icons.pin;
          return (
            <Card
              key={a.id}
              padding={14}
              style={{ display: "flex", alignItems: "flex-start", gap: 12 }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "var(--surface-2)",
                  display: "grid",
                  placeItems: "center",
                  color: "var(--ink-1)",
                  flexShrink: 0,
                }}
              >
                <Icon size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="display" style={{ fontSize: 14, fontWeight: 700 }}>
                    {a.name || label}
                  </span>
                  {a.isPrimary && <Badge tone="primary">{t("v2.common.default")}</Badge>}
                </div>
                <div
                  style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 2 }}
                >
                  {formatLine(a)}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/addresses/new?id=${encodeURIComponent(a.id)}`);
                }}
                aria-label={t("v2.address.edit_aria")}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "transparent",
                  border: 0,
                  color: "var(--ink-3)",
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Icons.edit size={14} />
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (deletingId) return;
                  if (!window.confirm(t("v2.address.delete_confirm"))) return;
                  setDeletingId(a.id);
                  try {
                    await deleteAddress(a.id);
                  } catch (err) {
                    console.warn("[SavedAddresses] delete failed", err);
                    window.alert(t("v2.address.delete_failed"));
                  } finally {
                    setDeletingId(null);
                  }
                }}
                aria-label={t("v2.address.delete_aria")}
                disabled={deletingId === a.id}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: "transparent",
                  border: 0,
                  color: "#b91c1c",
                  cursor: deletingId === a.id ? "default" : "pointer",
                  opacity: deletingId === a.id ? 0.5 : 1,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Icons.trash size={14} />
              </button>
            </Card>
          );
        })}

        <button
          onClick={() => navigate("/specify-location?returnTo=new-address-form")}
          style={{
            padding: 14,
            borderRadius: "var(--radius-card)",
            border: "1px dashed var(--border-strong)",
            background: "transparent",
            color: "var(--ink-2)",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Icons.plus size={16} /> {t("v2.address.add_new")}
        </button>
      </div>
    </div>
  );
}
